/**
 * Centralized API Error Handler
 * 
 * Provides consistent error handling for:
 * - PLAN_LIMIT_EXCEEDED errors (warehouse/vehicle limits)
 * - Warehouse not found / archived errors
 * - Generic API errors
 */

import { toast } from 'sonner';

// Plan upgrade suggestions by resource type
const UPGRADE_SUGGESTIONS = {
  warehouse: {
    Growth: 'Upgrade to Growth for more.',
    Pro: 'Upgrade to Pro for more.',
    default: 'Upgrade your plan for more.'
  },
  vehicle: {
    Growth: 'Upgrade to Growth for more.',
    Pro: 'Upgrade to Pro for more.',
    default: 'Upgrade your plan for more.'
  }
};

// Toast throttling - track active plan limit toasts by resource type
const activePlanLimitToasts = new Map();
const TOAST_THROTTLE_MS = 3000; // Minimum time between toasts for same resource

/**
 * Check if error is a plan limit exceeded error
 */
export const isPlanLimitError = (error) => {
  return error?.response?.data?.detail?.code === 'PLAN_LIMIT_EXCEEDED';
};

/**
 * Check if error is a warehouse not found/archived error
 */
export const isWarehouseUnavailableError = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  
  if (status === 404) {
    const detailLower = (typeof detail === 'string' ? detail : '').toLowerCase();
    return detailLower.includes('warehouse not found') || 
           detailLower.includes('warehouse has been archived') ||
           detailLower.includes('warehouse is unavailable');
  }
  return false;
};

/**
 * Get user-friendly message for plan limit error
 * Clamps display count to avoid showing numbers exceeding limit
 */
export const getPlanLimitMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (!detail || detail.code !== 'PLAN_LIMIT_EXCEEDED') {
    return 'Resource limit reached. Please upgrade your plan.';
  }
  
  const { resource, limit, current_count, required_plan } = detail;
  const resourcePlural = resource === 'warehouse' ? 'warehouses' : 'vehicles';
  
  // Clamp display count to never exceed limit (avoids confusing "5 of 3" messages)
  const displayCount = Math.min(current_count, limit);
  
  const upgradeSuggestion = UPGRADE_SUGGESTIONS[resource]?.[required_plan] || 
                            UPGRADE_SUGGESTIONS[resource]?.default ||
                            `Upgrade to ${required_plan} for more.`;
  
  return `Limit reached (${displayCount} of ${limit} ${resourcePlural}). ${upgradeSuggestion}`;
};

/**
 * Get short limit message for tooltips/compact displays
 */
export const getShortLimitMessage = (planLimits, resource) => {
  if (!planLimits?.[resource]) return null;
  
  const { limit, current_count, can_create, required_plan_for_more } = planLimits[resource];
  
  if (can_create || limit === null) return null;
  
  const displayCount = Math.min(current_count, limit);
  return `Limit reached (${displayCount}/${limit}). Upgrade to add more.`;
};

/**
 * Show plan limit toast with optional Upgrade CTA
 * Throttled to prevent stacking multiple toasts
 * 
 * @param {string} message - The toast message
 * @param {string} resource - 'warehouse' or 'vehicle' (for throttling)
 * @param {object} options - { canUpgrade, onUpgrade }
 */
export const showPlanLimitToast = (message, resource, options = {}) => {
  const { canUpgrade = false, onUpgrade = null } = options;
  
  // Check if we recently showed a toast for this resource
  const lastShown = activePlanLimitToasts.get(resource);
  const now = Date.now();
  
  if (lastShown && (now - lastShown) < TOAST_THROTTLE_MS) {
    // Throttled - don't show duplicate toast
    return;
  }
  
  // Update last shown time
  activePlanLimitToasts.set(resource, now);
  
  // Dismiss any existing plan limit toast for this resource
  const toastId = `plan-limit-${resource}`;
  toast.dismiss(toastId);
  
  // Show toast with or without action button
  if (canUpgrade && onUpgrade) {
    toast.error(message, {
      id: toastId,
      duration: 5000,
      action: {
        label: 'Upgrade',
        onClick: onUpgrade
      }
    });
  } else {
    toast.error(message, {
      id: toastId,
      duration: 5000
    });
  }
};

/**
 * Format API error into user-friendly message
 */
export const formatApiError = (error, defaultMessage = 'An error occurred') => {
  // Plan limit error
  if (isPlanLimitError(error)) {
    return getPlanLimitMessage(error);
  }
  
  // Warehouse unavailable
  if (isWarehouseUnavailableError(error)) {
    return 'Selected warehouse is unavailable.';
  }
  
  // Generic error with detail
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail?.message) {
    return detail.message;
  }
  
  // Fallback
  return defaultMessage;
};

/**
 * Handle API error with appropriate toast
 * Returns true if error was handled, false otherwise
 */
export const handleApiError = (error, options = {}) => {
  const { 
    defaultMessage = 'An error occurred',
    showToast = true,
    onPlanLimit = null,
    onWarehouseUnavailable = null
  } = options;
  
  // Skip if request was queued for offline
  if (error?.isQueued) {
    return true;
  }
  
  // Plan limit error
  if (isPlanLimitError(error)) {
    const message = getPlanLimitMessage(error);
    if (showToast) {
      toast.error(message, { duration: 5000 });
    }
    if (onPlanLimit) {
      onPlanLimit(error.response.data.detail);
    }
    return true;
  }
  
  // Warehouse unavailable
  if (isWarehouseUnavailableError(error)) {
    if (onWarehouseUnavailable) {
      onWarehouseUnavailable();
    }
    // Toast will be shown by warehouse fallback mechanism
    return true;
  }
  
  // Generic error
  if (showToast) {
    const message = formatApiError(error, defaultMessage);
    toast.error(message);
  }
  
  return false;
};

/**
 * Pre-check if a resource can be created based on plan limits
 * Returns { canCreate, message, displayCount, limit }
 * displayCount is clamped to never exceed limit
 */
export const checkPlanLimit = (planLimits, resource) => {
  if (!planLimits?.[resource]) {
    return { canCreate: true, message: null, displayCount: null, limit: null };
  }
  
  const { limit, current_count, can_create, required_plan_for_more } = planLimits[resource];
  
  // Clamp display count to never exceed limit
  const displayCount = limit !== null ? Math.min(current_count, limit) : current_count;
  
  if (can_create) {
    return { canCreate: true, message: null, displayCount, limit };
  }
  
  const resourcePlural = resource === 'warehouse' ? 'warehouses' : 'vehicles';
  
  const upgradeSuggestion = required_plan_for_more 
    ? `Upgrade to ${required_plan_for_more} for more.`
    : 'Upgrade your plan for more.';
  
  const message = `Limit reached (${displayCount} of ${limit} ${resourcePlural}). ${upgradeSuggestion}`;
  
  return { canCreate: false, message, displayCount, limit };
};

export default {
  isPlanLimitError,
  isWarehouseUnavailableError,
  getPlanLimitMessage,
  getShortLimitMessage,
  showPlanLimitToast,
  formatApiError,
  handleApiError,
  checkPlanLimit
};
